import { createSelector, defaultMemoize, createSelectorCreator } from 'reselect';
import moment from 'moment';
import { selectors as groupSelectors } from '../groups';
import { selectors as membershipSelectors } from '../memberships';
import { selectors as activityReportSelectors } from '../activity-reports';
import { selectors as activityTypeSelectors } from '../activity-types';
import { selectCurrentUserId } from '../current-user/selectors';
import { selectors as skillSelectors } from '../skills';
import { selectors as dutyFunctionSelectors } from '../standby-duty-functions';
import { selectors as taskSelectors } from '../tasks';
import { ignoredActivityTypes } from '../dashboards/selectors';
import { orderBy, isEqual, min, compact, uniq } from 'lodash';
import dummyIncident from './dummy-incident';
import createMetadataAPISelectors from '../../utils/api-metadata-selectors';

const defaultSelectors = createMetadataAPISelectors({ name: 'incidents' });

const selectIncidentsState = state => state.incidents.byId;

const currentDashboard = state => state.dashboards.byId[state.dashboards.currentDashboardId];

const empty = []; // static value to return to avoid recalculations

// This function creates special selectors that perform a deep equals on the
// input parameters, instead of an equality check (===). In other words, it will
// return the previously computed value if arguments are deep equal, which reduces the number of updates.
const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

export const activeIncidents = createSelector(
  selectIncidentsState,
  groupSelectors.byId,
  ignoredActivityTypes,
  taskSelectors.byId,
  state => state.appTime,
  currentDashboard,
  (incidentsById, groupsById, ignoredActivityTypeIds, tasksById, appTime, currentDashboard) => {
    const isIgnoredIncident = incident =>
      (incident.activity_type_ids || []).some(id => ignoredActivityTypeIds.includes(id));

    const augmentWithStations = incident => {
      const stations = incident.groups
        .map(groupId => {
          const group = groupsById[groupId];
          if (!group) return null;
          return group.type === 'team' ? groupsById[group.parent_group_id] : group;
        })
        .filter(Boolean);
      return {
        ...incident,
        stations: uniq(stations)
      };
    };

    const augmentWithTasks = incident => ({
      ...incident,
      tasks: incident.task_ids.map(id => tasksById[id]).filter(Boolean)
    });

    const isRecentIncident = incident => {
      const elapsedTime = moment(appTime).diff(incident.created_at, 'seconds');

      let displayDuration = currentDashboard.show_incidents_for_minutes * 60;
      if (!displayDuration) displayDuration = 600;
      return elapsedTime < displayDuration;
    };

    const result = Object.values(incidentsById)
      .filter(incident => incident.type === 'incident_alert')
      .filter(incident => !incident.transient)
      .filter(incident => !isIgnoredIncident(incident))
      .map(incident => augmentWithStations(incident))
      .filter(incident => isRecentIncident(incident))
      .map(incident => augmentWithTasks(incident));

    // return same empty array to avoid recalculations
    return result.length === 0 ? empty : result;
  }
);

export const displayedIncidentId = state => state.dashboards.displayedIncidentId;
const dashboardEditMode = state => state.dashboards.editMode;

// returns a map from groupId to a map from incident id to incident
// basically a grouping of incidents per group
export const groupToIncidentsMap = createSelector(activeIncidents, activeIncidents => {
  const result = {};

  for (const incident of activeIncidents) {
    for (const groupId of incident.groups) {
      result[groupId] = result[groupId] || {};
      result[groupId][incident.id] = incident;
    }
  }

  return result;
});

export const selectDisplayableIncidents = createDeepEqualSelector(groupToIncidentsMap, activeIncidents => {
  let incidents = {};
  Object.values(activeIncidents).map(groupIncidents => (incidents = { ...incidents, ...groupIncidents }));
  return orderBy(Object.values(incidents), ['created_at'], ['desc']);
});

const selectCurrentDashboardGroup = state => {
  const dashboard = state.dashboards.byId[state.dashboards.currentDashboardId];
  if (!dashboard) return null;

  const group = state.groups.byId[dashboard.group_id];
  return group;
};

export const selectCurrentDisplayedIncident = createSelector(
  selectDisplayableIncidents,
  displayedIncidentId,
  dashboardEditMode,
  skillSelectors.byId,
  dutyFunctionSelectors.byId,
  membershipSelectors.byId,
  selectCurrentDashboardGroup,
  (activeIncidents, incidentId, editMode, skillsById, dutyFunctionsById, membershipsById, group) => {
    // avoid a non-displayable incident being selected. This can happen when a
    // filtered out incident comes in. The displayedIncidentId is set in the
    // reducer to the latest incident, which is incorrect
    const activeIncident = activeIncidents.find(incident => incident.id === incidentId) || activeIncidents[0];

    let displayedIncident = null;

    if (activeIncident) {
      displayedIncident = activeIncident;
    } else if (editMode) {
      // if in edit mode, we want to display a dummy incident to show the user
      // what the incident widget will look like
      displayedIncident = dummyIncident(Object.values(skillsById), Object.values(dutyFunctionsById), group);
    }

    if (!displayedIncident) return null;

    const emptyMembership = { skills: [] };

    displayedIncident.incident_responses = (displayedIncident.incident_responses || []).map(r => {
      const skill_ids = (membershipsById[r.membership_id] || emptyMembership).skills;

      return {
        ...r,
        activeStandbyDutyFunctions: r.active_duty_function_ids.map(id => dutyFunctionsById[id]),
        skill_ids,
        skills: skill_ids.map(id => skillsById[id]).filter(Boolean)
      };
    });

    return displayedIncident;
  }
);

let currentStationToIncidentsMap = {};

// Returns an Object with Station ID => incidents. This is different from
// incidentsByGroupId, because it groups incidents belonging to Teams with the
// parent Station
export const stationToIncidentsMap = createDeepEqualSelector(
  activeIncidents,
  groupSelectors.byId,
  (activeIncidents, groupsById) => {
    const result = {};

    for (const incident of activeIncidents) {
      for (let groupId of incident.groups) {
        const group = groupsById[groupId];
        groupId = group?.type === 'team' ? group.parent_group_id : groupId;
        result[groupId] = result[groupId] || {};
        result[groupId][incident.id] = incident;
      }
    }

    if (!isEqual(result, currentStationToIncidentsMap)) {
      // avoid too many state updates by doing a deep equals and return previous
      // value if equal
      currentStationToIncidentsMap = result;
    }
    return currentStationToIncidentsMap;
  }
);

export const anyActiveIncidents = createSelector(activeIncidents, activeIncidents => activeIncidents.length > 0);

export const activeIncidentMembershipIds = createSelector(activeIncidents, activeIncidents =>
  uniq(activeIncidents.map(incident => incident.incident_responses || [])).flatMap(r => r.membership_id)
);

// This selector produces an augmented skill assignment to be
// consumed by the Skill Assignment's widget, sorted by skill
// priority
export const selectIncidentSkillAssignments = createSelector(
  selectCurrentDisplayedIncident,
  skillSelectors.byId,
  (incident, skillsById) => {
    const assignments = incident?.incident_skill_assignments || [];

    if (assignments.length === 0) return [];

    const responses = incident.incident_responses;

    const assignmentRows = assignments.map(assignment => {
      const response = responses.find(r => r.user_id === assignment.user_id);

      if (!response) return;
      if (response?.status === 'rejected') return;

      const aSkills = compact(assignment.skill_ids.map(sid => skillsById[sid])).filter(Boolean);
      const skillsByPrio = orderBy(aSkills, 'order', 'asc');
      // We will order the assignments by the highest priority skill of each
      // assignment
      const order = min(skillsByPrio.map(skill => skill.order));

      return {
        id: assignment.id,
        name: response?.user_name,
        status: response?.status,
        skills: skillsByPrio,
        order: order
      };
    });

    return orderBy(assignmentRows.filter(Boolean), 'order', 'asc');
  }
);

export const allReports = createSelector(
  defaultSelectors.values,
  activityReportSelectors.values,
  activityTypeSelectors.byId,
  selectCurrentUserId,
  (incidents, activityReports, activityTypesById, currentUserId) =>
    orderBy([...incidents, ...activityReports], ['created_at'], ['desc']).map(report => {
      const attendances = report.incident_responses || report.attendances || [];
      const userAttendance = attendances.find(e => e.user_id === currentUserId);
      const current_user_attendance_status = userAttendance?.reported_status;

      return {
        title: report.title || report.body,
        activity_types: report.activity_type_ids.map(id => activityTypesById[id]).filter(Boolean),
        current_user_attendance_status,
        ...report
      };
    })
);

export default {
  ...defaultSelectors,
  anyActiveIncidents,
  groupToIncidentsMap,
  selectCurrentDisplayedIncident,
  selectDisplayableIncidents,
  activeIncidentMembershipIds,
  allReports
};
